import { Users, ShipWheel, WifiOff, Anchor, CalendarCheck, UtensilsCrossed } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal, Overline } from "@/components/motion";
import { IMAGES } from "@/data/site";

const REASONS = [
  { icon: Users, title: "Nimeni nu stă deoparte", text: "Pe un velier, fiecare manevră cere coordonare: cineva la cârmă, cineva la pânze, cineva la ghiu. Echipa se formează singură." },
  { icon: ShipWheel, title: "Lideri noi la cârmă", text: "Fiecare coleg primește tura lui la cârmă. Vezi imediat cine preia conducerea când contează." },
  { icon: WifiOff, title: "Deconectare reală", text: "În larg, telefoanele tac de la sine. Conversațiile bune încep exact atunci." },
  { icon: Anchor, title: "O destinație memorabilă", text: "Balcik nu e o sală de conferințe. E orașul alb de pe stânci, cu taverne pe faleză și apusuri greu de uitat." },
];

const DAYS = [
  {
    day: "Vineri",
    title: "Plecare spre sud",
    desc: "Îmbarcare dimineața în Marina Life Harbour, Limanu. Navigație cu pânzele de-a lungul coastei, oprire pentru baie și prânz la bord. Seara: acostare în Balcik și cină la o tavernă pe faleză.",
  },
  {
    day: "Sâmbătă",
    title: "Balcik & exerciții de echipă",
    desc: "Dimineața: Palatul Reginei Maria și Grădina Botanică. După-amiaza: sesiune practică în golf — viraje, oforare și fiecare coleg la cârmă. Seara liberă în orașul vechi, noapte la bord.",
  },
  {
    day: "Duminică",
    title: "Retur la apus",
    desc: "Cafeaua pe punte, ultimele fotografii cu stâncile și navigăm înapoi spre Limanu, cu soarele în pupa. Sosire la apus — luni sunteți alți oameni la birou.",
  },
];

const TeamBuilding = () => (
  <div data-testid="teambuilding-page">
    <PageHero
      testid="teambuilding-hero"
      image={IMAGES.balchik}
      alt="Stâncile de la Cap Kaliakra, destinația excursiei de team building"
      overline="Team Building · Balcik · Weekend"
      titleLines={["Echipa se leagă", "pe mare."]}
      subtitle="Weekend de 2 zile cu velierul Hope: plecare vineri din Limanu, întoarcere duminică la apus. Pe mare, ierarhiile rămân pe uscat — iar echipa devine echipaj."
    />

    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <Reveal className="mb-16">
        <Overline className="mb-6">De Ce pe Velier</Overline>
        <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
          Sala de ședințe <span className="italic text-gold">nu are pânze.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
        {REASONS.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08} className="bg-abyss">
            <div className="h-full p-8 lg:p-10" data-testid={`tb-reason-${i + 1}`}>
              <item.icon className="h-5 w-5 text-gold" />
              <h3 className="mt-5 font-serif text-xl text-ivory">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{item.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-y border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16">
          <Overline className="mb-6">Programul Weekendului</Overline>
          <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl">
            Vineri — Duminică, <span className="italic text-gold">Limanu → Balcik → Limanu.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {DAYS.map((d, i) => (
            <Reveal key={d.day} delay={i * 0.12}>
              <div className="flex h-full flex-col border border-white/10 p-8 transition-colors duration-500 hover:border-gold/40 lg:p-10" data-testid={`tb-day-${d.day.toLowerCase()}`}>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{d.day}</p>
                <h3 className="mt-5 font-serif text-2xl text-ivory">{d.title}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-mist">{d.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-gold/30 p-8 sm:flex-row sm:items-center lg:p-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Tarif orientativ</p>
              <p className="mt-3 font-serif text-3xl font-light text-ivory">
                De la 100 € <span className="text-lg text-mist">/ persoană / weekend</span>
              </p>
            </div>
            <div className="max-w-md text-sm leading-relaxed text-mist">
              <p className="flex items-center gap-3">
                <CalendarCheck className="h-4 w-4 shrink-0 text-gold" /> Grupuri de 6–10 colegi, weekenduri din mai până în octombrie 2027
              </p>
              <p className="mt-3 flex items-center gap-3">
                <UtensilsCrossed className="h-4 w-4 shrink-0 text-gold" /> Include skipper, combustibil, cazare la bord și programul de echipă
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaBanner
      title="Echipa voastră merită un weekend altfel."
      subtitle="Spune-ne câți colegi veniți și ce weekend vizați — construim programul împreună."
    />
  </div>
);

export default TeamBuilding;
